import { Avatar, Badge, Dropdown } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import CoinIcon from 'assets/Header/CoinIcon';
import GemIcon from 'assets/Header/GemIcon';
import ProfileIcons from 'assets/Icons/ProfileIcons';
import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { FC, useEffect, useMemo } from 'react';
import { BiMenu, BiX } from 'react-icons/bi';
import { useRecoilState } from 'recoil';
import { twMerge } from 'tailwind-merge';

import { EventDetails, logEvent } from '@/hooks/useClickEventTracking';

import RenderIf from '@/components/Common/RenderIf';
import CountrySelector from '@/components/Layout/CountrySelector';
import MenuButton from '@/components/Layout/Header/Menus/MenuButton';
import Button, {
  ButtonVariants,
} from '@/components/shared/basic/button/button';
import { getUserInitials } from '@/components/shared/getInitials';

import { authModalControl } from '@/atoms/authModalControl';
import { isSubscriptionDisabled } from '@/atoms/countryState';
import { aiModalOpenAtomGlobal } from '@/atoms/pdfStates/aiModalOpenAtom';
import { user } from '@/atoms/user';
import { isPlatformSubscriptionActive } from '@/utils/index';
import { User } from '@/utils/interfaces/User';

interface Props {
  keyCount: number;
  loggedIn: boolean;
  shouldRenderLinks: boolean;
  validPathSearch: string[];
  chatCountValue: number;
  hasNewBook: boolean;
  dropdownMenu: React.JSX.Element;
  showDropDown: boolean;
  setShowDropDown: React.Dispatch<React.SetStateAction<boolean>>;
  showDrawer: () => void;
  dropdownRef: React.MutableRefObject<HTMLDivElement | null>;
  userName: string | undefined;
  isTrueCheckoutPage: boolean;
  isCartPage: boolean;
  isPlatformCheckoutPage: boolean;
}

const Menus: FC<Props> = ({
  isTrueCheckoutPage,
  isCartPage,
  isPlatformCheckoutPage,
  keyCount,
  loggedIn,
  shouldRenderLinks,
  validPathSearch,
  chatCountValue,
  hasNewBook,
  dropdownMenu,
  showDropDown,
  setShowDropDown,
  showDrawer,
  dropdownRef,
  userName,
}) => {
  const router = useRouter();
  const [openDrawerGlobal] = useRecoilState<boolean>(aiModalOpenAtomGlobal);
  const [userData] = useRecoilState<User | undefined>(user);
  const isCheckoutPage = !router.asPath.includes('/checkout');
  const [, setVisible] = useRecoilState(authModalControl);

  const screens = useBreakpoint();

  const gradientBg = 'linear-gradient(180deg, #FF5612 0%, #C72707 100%)';
  const avatarSrc =
    userData?.avatar &&
    `${process.env.NEXT_PUBLIC_IMAGE_BASE_URL_ECOM}/${userData?.avatar}`;
  const avatarBackground = useMemo(
    () => (!avatarSrc ? gradientBg : undefined),
    [avatarSrc, gradientBg],
  );


  useEffect(() => {
    if (showDropDown) {
      const eventParams: EventDetails = {
        event_name: 'screen_view',
        source: 'global',
        source_type: 'menu',
        source_id: null,
        sub_source: null,
        sub_source_id: null,
        unit: 'boolean',
        value: 1,
      };

      logEvent({
        eventDetails: eventParams,
        userData: userData,
        pathname: router.pathname,
      });
    }
  }, [showDropDown])

  return (
    <div className='flex gap-2'>
      <RenderIf
        condition={
          router?.pathname !== '/zai' &&
          !isSubscriptionDisabled() &&
          !openDrawerGlobal &&
          userData &&
          userData?.features?.length > 0 &&
          // twScreens.xxs &&
          !isTrueCheckoutPage &&
          !isCartPage &&
          !isPlatformCheckoutPage
        }
      >
        <Link href='/byob'>
          <MenuButton icon={<GemIcon />} title={keyCount} />
        </Link>
      </RenderIf>
      <RenderIf
        condition={
          Boolean(userData?.organization && userData?.organization?.role === 'admin') &&
          !openDrawerGlobal &&
          !isTrueCheckoutPage &&
          !isCartPage &&
          !isPlatformCheckoutPage
        }
      >
        <Link href='/corporate/wallet'>
          <MenuButton
            icon={<span className='text-base leading-none'>💵</span>}
            title={
              userData?.organization?.wallet_balance !== undefined
                ? `₹${(Number(userData?.organization?.wallet_balance) / 100).toLocaleString('en-IN')}`
                : 'Business'
            }
          />
        </Link>
      </RenderIf>
      <RenderIf
        condition={
          shouldRenderLinks &&
          router.asPath !== '/contact/' &&
          !router.asPath.includes('/broadcast-message/') &&
          !router.pathname.includes('/books/[bookId]') &&
          validPathSearch.includes(router.pathname) &&
          !openDrawerGlobal &&
          loggedIn &&
          !isTrueCheckoutPage &&
          !isCartPage &&
          !isPlatformCheckoutPage
        }
      >
        <Link href='/rewards'>
          <MenuButton
            className='xl:ps-3 ps-[9px] xl:pe-[14px] pe-[11px]'
            icon={<CoinIcon />}
            title={userData?.balance || 0}
          />
        </Link>
      </RenderIf>
      <CountrySelector loggedIn={loggedIn} />
      <RenderIf condition={loggedIn}>
        <Badge
          dot
          color='#FF5612'
          count={chatCountValue || hasNewBook ? 1 : 0}
          className='customDot'
          style={{ color: 'white', fontSize: '11px' }}
        >
          <Dropdown
            overlay={dropdownMenu}
            trigger={['click']}
            open={screens.md ? showDropDown : false}
            onOpenChange={(visible) =>
              screens.md ? setShowDropDown(visible) : showDrawer()
            }
            overlayStyle={{
              width: '390px',
            }}
          >
            <AnimatePresence>
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                ref={dropdownRef}
                onClick={(e) => {
                  e.preventDefault();
                  screens.md ? setShowDropDown(!showDropDown) : showDrawer();
                }}
                className={twMerge(
                  'xl:h-10 h-8 rounded-full bg-grayZl-900 flex xl:gap-1.5 gap-1 items-center ps-1 pe-2  transition-all hover:bg-grayZl-850 ',
                  showDropDown ? 'bg-grayZl-850' : '',
                )}
              >
                <Avatar
                  alt='profile-picture'
                  style={{
                    fontSize: '10px',
                    border: isPlatformSubscriptionActive(userData)
                      ? '2px solid #D3A984'
                      : '1px solid #FFFFFF0D',
                    background: avatarBackground,
                  }}
                  src={avatarSrc}
                  className='xl:w-8 w-6 xl:h-8 h-6'
                >
                  {' '}
                  {!avatarSrc && !userName ? (
                    <ProfileIcons width='8' />
                  ) : (
                    userName && getUserInitials(userName)
                  )}
                </Avatar>
                <div className='relative xl:w-6 xl:h-6 w-5 h-5'>
                  {/* Menu icon */}
                  <BiMenu
                    className={twMerge(
                      'absolute inset-0 xl:w-6 xl:h-6 w-5 h-5 text-grayZl-500',
                      'transition-all duration-300 ease-in-out transform',
                      showDropDown
                        ? 'opacity-0 rotate-180 scale-50'
                        : 'opacity-100 rotate-0 scale-100',
                    )}
                  />

                  {/* Close icon */}
                  <BiX
                    className={twMerge(
                      'absolute inset-0 xl:w-6 xl:h-6 w-5 h-5 text-grayZl-500',
                      'transition-all duration-300 ease-in-out transform',
                      showDropDown
                        ? 'opacity-100 rotate-0 scale-100'
                        : 'opacity-0 -rotate-180 scale-50',
                    )}
                  />
                </div>
              </motion.div>
            </AnimatePresence>
          </Dropdown>
        </Badge>
      </RenderIf>
      <RenderIf
        condition={
          !loggedIn && (isCheckoutPage || router.pathname === '/checkout-v2')
        }
      >
        <Button
          size={0}
          rounded={false}
          variant={ButtonVariants.PRIMARY}
          onClick={() =>
            setVisible((prevState) => ({
              ...prevState,
              login: true,
            }))
          }
          className='flex justify-center px-5   font-medium text-xs  whitespace-nowrap xl:h-10 h-8 rounded-lg transition-all hover:scale-105'
        >
          <span className='px-2 xxxs:px-3 lg:px-4 text-xs xxxs:text-sm font-medium text-white'>
            Sign In
          </span>
        </Button>
      </RenderIf>
    </div>
  );
};

export default Menus;
